import { inRange, isEqual, range } from 'lodash';
import { getRoot, types as t } from 'mobx-state-tree';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { findNodeByElementId } from '../../../../../BlogEditor/plugins/shared-transforms';
import {
  getSelectionRangeBound,
  getStartEndCellPositionFromColumnIndex,
  getStartEndCellPositionFromRowIndex,
  isMouseEvent,
} from './types';

const CellSelectionInfo = t.model('CellSelectionInfo', {
  columnIndex: t.number,
  rowIndex: t.number,
  colSpan: t.maybeNull(t.number),
  rowSpan: t.maybeNull(t.number),
});

export const TableAdminViewCellSelectionStore = t
  .model('TableAdminViewCellSelectionStore', {
    isDrag: t.optional(t.boolean, false),
    isPressShiftKey: t.optional(t.boolean, false),

    startDragPosition: t.maybeNull(CellSelectionInfo),
    endDragPosition: t.maybeNull(CellSelectionInfo),

    startHoverPosition: t.maybeNull(CellSelectionInfo),
    endHoverPosition: t.maybeNull(CellSelectionInfo),
  })
  .views((self) => ({
    get selectionRangeBound() {
      const rootNode: any = getRoot(self);

      return getSelectionRangeBound({
        startPosition: self.startDragPosition,
        endPosition: self.endDragPosition,
        tableNode: rootNode.tableNode,
      });
    },

    get hoverSelectionRangeBound() {
      const rootNode: any = getRoot(self);

      return getSelectionRangeBound({
        startPosition: self.startHoverPosition,
        endPosition: self.endHoverPosition,
        tableNode: rootNode.tableNode,
      });
    },
  }))
  .views((self) => ({
    get selectedColumnIndexes() {
      const { topLeft, bottomRight } = self.selectionRangeBound;
      const root: any = getRoot(self);
      if (
        topLeft &&
        bottomRight &&
        !isEqual(topLeft, bottomRight) &&
        topLeft.rowIndex === 0 &&
        bottomRight.rowIndex === root.rowCount - 1
      ) {
        return range(topLeft.columnIndex, bottomRight.columnIndex + 1);
      }
      return [];
    },
    get selectedRowIndexes() {
      const { topLeft, bottomRight } = self.selectionRangeBound;
      const root: any = getRoot(self);
      if (
        topLeft &&
        bottomRight &&
        !isEqual(topLeft, bottomRight) &&
        topLeft.columnIndex === 0 &&
        bottomRight.columnIndex === root.columnCount - 1
      ) {
        return range(topLeft.rowIndex, bottomRight.rowIndex + 1);
      }
      return [];
    },
  }))
  .views((self) => ({
    isInRange({ rowIndex, columnIndex, rangeBound }) {
      const { topLeft, bottomRight } = rangeBound;
      if (topLeft && bottomRight) {
        return (
          inRange(rowIndex, topLeft.rowIndex, bottomRight.rowIndex + 1) &&
          inRange(columnIndex, topLeft.columnIndex, bottomRight.columnIndex + 1)
        );
      }
      return false;
    },
    isLastCellSelected({ rowIndex, columnIndex }) {
      if (self.endDragPosition) {
        return (
          self.endDragPosition.columnIndex === columnIndex &&
          self.endDragPosition.rowIndex === rowIndex
        );
      }
      return false;
    },
  }))
  .views((self) => ({
    get isSelectSingleCell() {
      return self.startDragPosition && isEqual(self.startDragPosition, self.endDragPosition);
    },
    get allSelectedCells() {
      const root: any = getRoot(self);
      const cellPaths = [];
      range(root.rowCount).forEach((rowIndex) => {
        range(root.columnCount).forEach((columnIndex) => {
          if (self.isInRange({ rowIndex, columnIndex, rangeBound: self.selectionRangeBound })) {
            cellPaths.push([...root.tablePath, rowIndex, columnIndex]);
          }
        });
      });

      return cellPaths;
    },
  }))
  .views((self) => ({
    isAbleToMerge() {
      return self.startDragPosition && !isEqual(self.startDragPosition, self.endDragPosition);
    },
    isAbleToSplit({ editor }) {
      const root: any = getRoot(self);
      if (self.startDragPosition && isEqual(self.startDragPosition, self.endDragPosition)) {
        const cellPath = [
          ...root.tablePath,
          self.startDragPosition.rowIndex,
          self.startDragPosition.columnIndex,
        ];
        const [cell] = Editor.node(editor, cellPath) as any;
        if (cell?.colSpan > 1 || cell?.rowSpan > 1) {
          return true;
        }
      }
      return false;
    },
  }))
  .actions((self) => ({
    clearSelection() {
      self.startDragPosition = null;
      self.endDragPosition = null;
    },
  }))
  .actions((self) => ({
    shiftPressStart() {
      self.isPressShiftKey = true;
    },
    shiftPressStop() {
      self.isPressShiftKey = false;
    },
    onDragStart() {
      self.isDrag = true;
    },
    onSelectStart({ event, rowIndex, columnIndex, element }) {
      if (event.nativeEvent && isMouseEvent(event.nativeEvent) && event.nativeEvent.which === 3) {
        return;
      }
      self.isDrag = true;
      self.startDragPosition = {
        rowIndex,
        columnIndex,
        rowSpan: element?.rowSpan || 1,
        colSpan: element?.colSpan || 1,
      };
      self.endDragPosition = {
        ...self.startDragPosition,
      };
    },
    onSelectStop() {
      self.isDrag = false;
    },
    mergeSelection({ rowIndex, columnIndex, editor, element }) {
      self.endDragPosition = {
        rowIndex,
        columnIndex,
        rowSpan: element?.rowSpan || 1,
        colSpan: element?.colSpan || 1,
      };

      const path = ReactEditor.findPath(editor, element);
      Transforms.select(editor, path);
    },

    mergeCell({ editor, tableId }) {
      const [, tablePath] = findNodeByElementId({ editor, elementId: tableId });
      const { topLeft, bottomRight } = self.selectionRangeBound;
      const calculateRowSpan = bottomRight.rowIndex - topLeft.rowIndex + 1;
      const calculateColumnSpan = bottomRight.columnIndex - topLeft.columnIndex + 1;

      Editor.withoutNormalizing(editor, () => {
        const setNodeAt = [...tablePath, topLeft.rowIndex, topLeft.columnIndex];
        Transforms.setNodes(
          editor,
          { rowSpan: calculateRowSpan, colSpan: calculateColumnSpan },
          { at: setNodeAt }
        );

        if (calculateColumnSpan > 1 || calculateRowSpan > 1) {
          // Remove nodes before merge cells
          range(bottomRight.rowIndex, topLeft.rowIndex - 1).forEach((rowIndex) => {
            range(bottomRight.columnIndex, topLeft.columnIndex - 1).forEach((colIndex) => {
              // don't remove top left node, since we want to expand it row and column
              if (!(rowIndex === topLeft.rowIndex && colIndex === topLeft.columnIndex)) {
                const cellPath = [...tablePath, rowIndex, colIndex];
                Transforms.setNodes(
                  editor,
                  { hidden: true, rowRef: topLeft.rowIndex, colRef: topLeft.columnIndex },
                  { at: cellPath }
                );
                Transforms.insertText(editor, '', {
                  at: [...cellPath, 0],
                });
              }
            });
          });
        }

        Transforms.select(editor, setNodeAt);
        ReactEditor.focus(editor);
      });

      self.clearSelection();
    },
    updateDragPosition({ startDragPosition, endDragPosition }) {
      self.startDragPosition = startDragPosition;
      self.endDragPosition = endDragPosition;
    },
    unMergeCell({ editor, tableId }) {
      const [, tablePath] = findNodeByElementId({ editor, elementId: tableId });
      const cellPath = [
        ...tablePath,
        self.startDragPosition.rowIndex,
        self.startDragPosition.columnIndex,
      ];
      const [cell] = Editor.node(editor, cellPath) as any;
      if (cell?.colSpan > 1 || cell?.rowSpan > 1) {
        Transforms.unsetNodes(editor, ['rowSpan', 'colSpan'], { at: cellPath });
      }
    },
    hoverRow(rowIdx) {
      const root: any = getRoot(self);
      const { startPosition, endPosition } = getStartEndCellPositionFromRowIndex({
        tableNode: root.tableNode,
        rowIndex: rowIdx,
        columnCount: root.columnCount,
      });

      self.startHoverPosition = startPosition;
      self.endHoverPosition = endPosition;
    },
    stopHoverRow() {
      self.startHoverPosition = null;
      self.endHoverPosition = null;
    },
    hoverColumn(colIdx) {
      const root: any = getRoot(self);
      const { startPosition, endPosition } = getStartEndCellPositionFromColumnIndex({
        tableNode: root.tableNode,
        columnIndex: colIdx,
        rowCount: root.rowCount,
      });
      self.startHoverPosition = startPosition;
      self.endHoverPosition = endPosition;
    },
    stopHoverColumn() {
      self.startHoverPosition = null;
      self.endHoverPosition = null;
    },
    selectRow(rowIdx) {
      const root: any = getRoot(self);
      const { startPosition, endPosition } = getStartEndCellPositionFromRowIndex({
        tableNode: root.tableNode,
        rowIndex: rowIdx,
        columnCount: root.columnCount,
      });

      self.startDragPosition = startPosition;
      self.endDragPosition = endPosition;
    },
    selectColumn(colIdx) {
      const root: any = getRoot(self);
      const { startPosition, endPosition } = getStartEndCellPositionFromColumnIndex({
        tableNode: root.tableNode,
        columnIndex: colIdx,
        rowCount: root.rowCount,
      });
      self.startDragPosition = startPosition;
      self.endDragPosition = endPosition;
    },
  }));
